'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

const UserProfileDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, userProfile, currentOrganization, currentMembership, signOut } = useAuth();
  const router = useRouter();

  const isAdminUser =
    currentMembership?.role === 'owner' || currentMembership?.role === 'admin';

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (!user || !userProfile) {
    return (
      <div className="flex items-center gap-2">
        <Link
          href="/login"
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-smooth hover:bg-accent/5 hover:text-accent"
        >
          Login
        </Link>
        <Link
          href="/signup"
          className="rounded-md bg-[#C9A961] px-4 py-1.5 text-sm font-bold text-[#0F172A] transition-all duration-200 hover:bg-[#e8c97a] hover:shadow-[0_0_12px_rgba(201,169,97,0.3)]"
        >
          Get Started
        </Link>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center space-x-3 rounded-md px-3 py-2 transition-smooth hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label="User profile menu"
        aria-expanded={isOpen}
      >
        <div className="relative h-8 w-8 overflow-hidden rounded-full">
          <AppImage
            src={userProfile.avatarUrl || '/assets/images/avatar-placeholder.png'}
            alt={`${userProfile.fullName || 'User'} avatar`}
            className="h-full w-full object-cover"
            width={32}
            height={32}
          />
        </div>
        <div className="hidden flex-col items-start lg:flex">
          <span className="font-body text-sm font-medium text-foreground">
            {userProfile.fullName || 'User'}
          </span>
          <span className="font-caption text-xs text-muted-foreground">{userProfile.role}</span>
        </div>
        <Icon
          name="ChevronDownIcon"
          size={16}
          className={`text-muted-foreground transition-smooth ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 rounded-md bg-popover shadow-elevation-3 transition-smooth">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-medium text-foreground">{userProfile.fullName || 'User'}</p>
            <p className="text-xs text-muted-foreground">{userProfile.email}</p>
            {currentOrganization && (
              <p className="mt-1 text-xs text-muted-foreground">{currentOrganization.name}</p>
            )}
            {isAdminUser && (
              <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#C9A961]/15 px-2 py-0.5 text-[10px] font-bold text-[#C9A961] uppercase tracking-wide">
                <Icon name="ShieldCheckIcon" size={10} />
                {currentMembership?.role}
              </span>
            )}
          </div>

          {/* Admin Console — owner/admin only */}
          {isAdminUser && (
            <div className="py-2 border-b border-border">
              <Link
                href="/admin-console"
                onClick={() => setIsOpen(false)}
                className="flex w-full items-center space-x-3 px-4 py-3 font-body text-sm transition-smooth hover:bg-muted text-[#C9A961] hover:text-[#e8c97a]"
              >
                <Icon name="CogIcon" size={18} className="text-[#C9A961]" />
                <div className="flex flex-col items-start">
                  <span className="font-semibold">Admin Console</span>
                  <span className="text-xs text-muted-foreground">System tools &amp; configuration</span>
                </div>
                <Icon name="ChevronRightIcon" size={14} className="ml-auto text-[#C9A961]/60" />
              </Link>
            </div>
          )}

          {/* Standard menu items */}
          <div className="py-2">
            <Link
              href="/settings"
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center space-x-3 px-4 py-3 font-body text-sm transition-smooth hover:bg-muted text-popover-foreground"
            >
              <Icon name="UserCircleIcon" size={18} className="text-muted-foreground" />
              <span>Account Settings</span>
            </Link>
            <Link
              href="/team-members-management"
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center space-x-3 px-4 py-3 font-body text-sm transition-smooth hover:bg-muted text-popover-foreground"
            >
              <Icon name="BuildingOfficeIcon" size={18} className="text-muted-foreground" />
              <span>Organization Settings</span>
            </Link>
            <Link
              href="/support"
              onClick={() => setIsOpen(false)}
              className="flex w-full items-center space-x-3 px-4 py-3 font-body text-sm transition-smooth hover:bg-muted text-popover-foreground"
            >
              <Icon name="QuestionMarkCircleIcon" size={18} className="text-muted-foreground" />
              <span>Help &amp; Support</span>
            </Link>
          </div>

          <div className="border-t border-border p-2">
            <button
              onClick={handleSignOut}
              className="flex w-full items-center space-x-2 rounded-md px-3 py-2 text-sm text-foreground transition-smooth hover:bg-muted"
            >
              <Icon name="ArrowRightOnRectangleIcon" size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;